import { useEffect, useRef, useState } from "react";
import SendIcon from "@mui/icons-material/Send";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { Box, Button, InputAdornment, MenuItem, TextField, Typography } from "@mui/material";
import { Modal } from "components/common/Modal";
import { pickupTimeSlots } from "constants/timeSlots";
import { JobDetailViewModel } from "./jobDetailTypes";

export type SelectedVendorSummary = {
  id: string;
  name: string;
  email: string;
  initial: string;
  color: string;
  priceLabel: string;
  priceInCents: number;
  proposedDate?: string;
  proposedTime?: string;
};

export type DirectAssignDetails = {
  amountInCents: number;
  scheduledAt: string;
  confirmedTime: string;
};

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  job: JobDetailViewModel;
  selectedVendors: SelectedVendorSummary[];
  onConfirm: (details?: DirectAssignDetails) => Promise<void>;
  submitting: boolean;
  directAssign?: boolean;
};

function formatEstimatedPrice(priceLabel: string, priceInCents: number): string | null {
  if (priceInCents <= 0) {
    return null;
  }

  return priceLabel;
}

function isoDateOrBlank(value?: string): string {
  if (!value) {
    return "";
  }

  const date = value.split("T")[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : "";
}

function knownTimeOrBlank(value?: string): string {
  if (!value || !(pickupTimeSlots as readonly string[]).includes(value)) {
    return "";
  }

  return value;
}

function centsToDollarInput(cents: number): string {
  if (!Number.isFinite(cents) || cents < 0) {
    return "";
  }

  const dollars = cents / 100;
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
}

function AssignmentSummary({
  directAssign,
  vendorName,
  vendorCount,
  vendorLabel,
}: {
  directAssign: boolean;
  vendorName: string;
  vendorCount: number;
  vendorLabel: string;
}) {
  if (directAssign) {
    return (
      <>
        <Box component="span" fontWeight={700} color="text.primary">
          {vendorName}
        </Box>{" "}
        will be assigned and marked vendor confirmed. A summary email will be sent to the vendor. The customer will
        still be asked to confirm this pickup.
      </>
    );
  }

  return (
    <>
      A quote request email will be sent to{" "}
      <Box component="span" fontWeight={700} color="text.primary">
        {vendorCount} {vendorLabel}
      </Box>{" "}
      with the following job details.
    </>
  );
}

function dollarsToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return null;
  }

  const dollars = Number(trimmed);
  if (!Number.isFinite(dollars) || dollars < 0) {
    return null;
  }

  return Math.round(dollars * 100);
}

export default function RequestQuoteConfirmationModal({
  open,
  setOpen,
  job,
  selectedVendors,
  onConfirm,
  submitting,
  directAssign = false,
}: Props) {
  const [priceDollars, setPriceDollars] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [confirmedTime, setConfirmedTime] = useState("");
  const initializedForOpen = useRef(false);
  const vendorCount = selectedVendors.length;
  const vendorLabel = vendorCount === 1 ? "vendor" : "vendors";
  const vendorName = selectedVendors[0]?.name ?? "the selected vendor";
  const amountInCents = dollarsToCents(priceDollars);
  const assignmentReady = amountInCents !== null && scheduledAt !== "" && confirmedTime !== "";

  useEffect(() => {
    if (!open) {
      initializedForOpen.current = false;
      return;
    }

    if (!directAssign || initializedForOpen.current) {
      return;
    }

    initializedForOpen.current = true;
    const vendor = selectedVendors[0];
    setPriceDollars(centsToDollarInput(vendor?.priceInCents ?? 0));
    setScheduledAt(isoDateOrBlank(vendor?.proposedDate) || isoDateOrBlank(job.pickupDate));
    setConfirmedTime(knownTimeOrBlank(vendor?.proposedTime) || knownTimeOrBlank(job.confirmedTime));
  }, [open, directAssign, job.confirmedTime, job.pickupDate, selectedVendors]);

  let confirmLabel = `Send ${vendorCount} Quote Request${vendorCount === 1 ? "" : "s"}`;
  let recipientLabel = "SENDING TO";
  if (directAssign) {
    confirmLabel = "Assign Vendor";
    recipientLabel = "ASSIGNING";
  }

  const handleConfirm = () => {
    if (directAssign) {
      if (amountInCents === null || !scheduledAt || !confirmedTime) {
        return;
      }

      onConfirm({
        amountInCents,
        scheduledAt,
        confirmedTime,
      }).then(() => {
        setOpen(false);
      });
      return;
    }

    onConfirm().then(() => {
      setOpen(false);
    });
  };

  return (
    <Modal open={open} setOpen={setOpen} width="min(560px, calc(100vw - 32px))" style={{ padding: "24px 28px 28px" }}>
      <Typography variant="h6" fontWeight={700} sx={{ fontSize: 20, mb: 2, color: "primary.main" }}>
        {directAssign ? "Assign Vendor?" : "Send Quote Requests?"}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
        <AssignmentSummary
          directAssign={directAssign}
          vendorName={vendorName}
          vendorCount={vendorCount}
          vendorLabel={vendorLabel}
        />
      </Typography>

      <Box
        sx={{
          backgroundColor: "#F5F5F5",
          borderRadius: "8px",
          p: 2,
          mb: 2.5,
        }}
      >
        {[
          { label: "Order", value: job.displayId },
          { label: "Client", value: job.client },
          { label: "Location", value: job.location },
          { label: "Requested date", value: job.pickupDate },
          { label: "Assets", value: `${job.assets} items` },
        ].map((row) => (
          <Box key={row.label} sx={{ display: "flex", gap: 2, mb: 0.75, "&:last-of-type": { mb: 0 } }}>
            <Typography variant="body2" color="text.secondary" sx={{ width: 120, flexShrink: 0 }}>
              {row.label}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {directAssign && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            mb: 2.5,
          }}
        >
          <TextField
            label="Pickup scheduled at"
            type="date"
            value={scheduledAt}
            onChange={(event) => setScheduledAt(event.target.value)}
            required
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Confirmed time"
            value={confirmedTime}
            onChange={(event) => setConfirmedTime(event.target.value)}
            required
            fullWidth
            size="small"
          >
            <MenuItem value="" disabled>
              Select a time
            </MenuItem>
            {pickupTimeSlots.map((timeSlot) => (
              <MenuItem key={timeSlot} value={timeSlot}>
                {timeSlot}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Price"
            value={priceDollars}
            onChange={(event) => setPriceDollars(event.target.value)}
            required
            fullWidth
            size="small"
            inputMode="decimal"
            placeholder="0"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
        </Box>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5, fontWeight: 700, letterSpacing: 0.8 }}
      >
        {recipientLabel}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
        {selectedVendors.map((vendor) => {
          const estimatedPrice = directAssign ? null : formatEstimatedPrice(vendor.priceLabel, vendor.priceInCents);

          return (
            <Box
              key={vendor.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: "8px",
                border: "1px solid #ECECEC",
                backgroundColor: "background.paper",
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  backgroundColor: vendor.color,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {vendor.initial}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={600} sx={{ fontSize: 14 }}>
                  {vendor.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                  {vendor.email}
                </Typography>
              </Box>
              {estimatedPrice && (
                <Typography fontWeight={700} sx={{ fontSize: 15, flexShrink: 0 }}>
                  {estimatedPrice}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {directAssign && !assignmentReady && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Enter a price, pickup scheduled at, and confirmed time to assign this vendor.
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button variant="outlined" onClick={() => setOpen(false)} disabled={submitting} sx={{ fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={directAssign ? <PersonAddAlt1Icon /> : <SendIcon />}
          onClick={handleConfirm}
          disabled={submitting || (directAssign && !assignmentReady)}
          sx={{ textTransform: "uppercase", fontWeight: 700 }}
        >
          {confirmLabel}
        </Button>
      </Box>
    </Modal>
  );
}
